'use client'

import { useEffect, useState } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { reloadPage } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { ConversationActionProps } from '@marketplace-web/messaging/conversation-action-data'
import { ConversationActionButton } from '@marketplace-web/messaging/conversation-action-feature'
import {
  RefundOfferAction,
  handleRefundOffer,
  getRefundOfferId,
  transformRefundOfferIdDto,
  partialRefundOffersClickEvent,
} from '@marketplace-web/complaints/complaints-data'

import GenericErrorModal from '../GenericErrorModal'
import DeclinePartialRefundOfferDialog from './DeclinePartialRefundOfferDialog'

enum DeclinePartialRefundOfferActiveDialog {
  DeclinePartialRefundDialog,
  ErrorDialog,
  None,
}

const DeclinePartialRefundOffer = ({
  primary,
  title,
  inverse,
  theme,
  size,
}: ConversationActionProps) => {
  const { track } = useTracking()

  const { transaction } = useConversationContext()

  const transactionId = transaction?.id?.toString()
  const complaintId = transaction?.complaintId

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [activeDialog, setActiveDialog] = useState(DeclinePartialRefundOfferActiveDialog.None)

  const {
    fetch: fetchRefundOfferId,
    transformedData: refundOfferId,
    isLoading: isRefundOfferIdLoading,
  } = useFetch(getRefundOfferId, response => transformRefundOfferIdDto(response)?.refundOfferId)

  useEffect(() => {
    if (
      activeDialog !== DeclinePartialRefundOfferActiveDialog.DeclinePartialRefundDialog ||
      !complaintId
    )
      return

    fetchRefundOfferId(Number(complaintId))
  }, [fetchRefundOfferId, activeDialog, complaintId])

  const handleDialogClose = () => {
    setActiveDialog(DeclinePartialRefundOfferActiveDialog.None)
  }

  const handleErrorDialogClose = () => {
    handleDialogClose()
    reloadPage()
  }

  const openDeclinePartialRefundOfferDialog = () => {
    setActiveDialog(DeclinePartialRefundOfferActiveDialog.DeclinePartialRefundDialog)
  }

  const trackDeclineOfferClick = () => {
    if (!transactionId || !complaintId) return

    const targetDetails = JSON.stringify({
      refund_offer_id: refundOfferId?.toString(),
    })

    track(
      partialRefundOffersClickEvent({
        target: 'partial_refund_offer_decline',
        transactionId,
        complaintId,
        targetDetails,
      }),
    )
  }

  const declinePartialRefundOffer = async () => {
    if (isSubmitting || !refundOfferId) return

    setIsSubmitting(true)

    const response = await handleRefundOffer({
      refundOfferId,
      action: RefundOfferAction.Reject,
    })

    trackDeclineOfferClick()

    if ('errors' in response) {
      setIsSubmitting(false)
      setActiveDialog(DeclinePartialRefundOfferActiveDialog.ErrorDialog)

      return
    }

    setIsSubmitting(false)
    handleDialogClose()
  }

  return (
    <>
      <ConversationActionButton
        text={title}
        primary={primary}
        size={size}
        theme={theme}
        inverse={inverse}
        onClick={openDeclinePartialRefundOfferDialog}
        testId="decline-partial-refund-offer-conversation-action"
      />
      <DeclinePartialRefundOfferDialog
        refundOfferId={refundOfferId}
        isDataLoading={isRefundOfferIdLoading}
        isOpen={activeDialog === DeclinePartialRefundOfferActiveDialog.DeclinePartialRefundDialog}
        onSubmit={declinePartialRefundOffer}
        onClose={handleDialogClose}
      />
      <GenericErrorModal
        isShown={activeDialog === DeclinePartialRefundOfferActiveDialog.ErrorDialog}
        onClose={handleErrorDialogClose}
      />
    </>
  )
}

export default DeclinePartialRefundOffer
